// login-frontend/src/components/Katie/utility/pageCollapse.js
// Full-page "falling bricks" collapse + optional follow-up fullscreen video.
// Requires: npm i html2canvas
import html2canvas from "html2canvas";

/**
 * Collapse the entire current page into falling tiles.
 * @param {Object} opts
 * @param {number} opts.rows - number of tile rows
 * @param {number} opts.cols - number of tile cols
 * @param {number} opts.duration - base fall duration per tile (ms)
 * @param {number} opts.randomDelay - max random delay per tile (ms)
 * @param {number} opts.gravity - extra translateY in px (beyond viewport)
 * @param {Function} [onComplete] - called when all tiles are gone
 */
export async function runPageCollapse(
    {
        rows = 8,
        cols = 12,
        duration = 1200,        // how long each tile “falls”
        randomDelay = 500,      // per-tile jitter
        gravity = 1200,         // how far it falls past the bottom
        rowStagger = 40,        // NEW: extra delay added per row (top rows drop first)
        overlayBg = "#000",     // NEW: background behind tiles while they fall
        holdMs = 100             // NEW: hold the final state before cleanup
    } = {},
    onComplete
) {
    // 1) Snapshot the full page
    const target = document.documentElement; // whole page
    const canvas = await html2canvas(target, {
        windowWidth: document.documentElement.scrollWidth,
        windowHeight: document.documentElement.scrollHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        backgroundColor: null,
        useCORS: true,
    });
    const dataURL = canvas.toDataURL("image/png");

    // 2) Overlay to hold the pieces
    const W = window.innerWidth;
    const H = window.innerHeight;
    const overlay = document.createElement("div");
    overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 999999;
    pointer-events: none; 
    overflow: hidden;
    background: ${overlayBg}; /* can be 'transparent' if you want to see the app behind */
  `;
    document.body.appendChild(overlay);

    // 3) Freeze the page behind (optional but nice)
    const pageRoot = document.getElementById("root") || document.body.firstElementChild;
    if (pageRoot) pageRoot.style.filter = "opacity(0.999)"; // force a paint so screenshot feels exact
    document.body.style.overflow = "hidden";

    // 4) Build tiles using the screenshot as background
    const tileW = Math.ceil(W / cols);
    const tileH = Math.ceil(H / rows);

    // Keyframes (once)
    const style = document.createElement("style");
    style.textContent = `
  @keyframes pageTileFall {
    0%   { transform: translate3d(0,0,0) rotate(0deg); opacity: 1; }
    100% { transform: translate3d(0, ${H + gravity}px, 0) rotate( ${/* random per-tile via inline */""}var(--rot, 15deg) ); opacity: 0.85; }
  }`;
    document.head.appendChild(style);

    let finished = 0;
    const total = rows * cols;

    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const left = c * tileW;
            const top = r * tileH;

            const piece = document.createElement("div");
            // Slight 1px overlap to avoid hairline gaps
            const bw = Math.min(tileW + 1, W - left);
            const bh = Math.min(tileH + 1, H - top);
            const delay = Math.random() * randomDelay + (r * rowStagger);
            const rot = (Math.random() * 40 - 20).toFixed(2) + "deg";

            piece.style.cssText = `
        position: absolute; left: ${left}px; top: ${top}px; width: ${bw}px; height: ${bh}px;
        background-image: url("${dataURL}");
        background-size: ${W}px ${H}px;
        background-position: -${left}px -${top}px;
        will-change: transform;
        animation: pageTileFall ${duration}ms ease-in forwards;
        animation-delay: ${delay}ms;
        --rot: ${rot};
        box-shadow: rgba(0,0,0,0.15) 0px 4px 10px;
      `;

            piece.addEventListener("animationend", () => {
                piece.remove();
                finished++;
                if (finished >= total) {
                    setTimeout(() => {
                        overlay.remove();
                        style.remove();
                        document.body.style.overflow = "";
                        if (typeof onComplete === "function") onComplete();
                    }, holdMs);
                }
            });

            overlay.appendChild(piece);
        }
    }
}

/**
 * Play a full-screen video once, then remove it.
 * @param {string} src - video URL (absolute or Vite asset via new URL(..., import.meta.url).href)
 * @param {Object} opts
 * @param {boolean} [opts.muted=false]
 * @param {number} [opts.fadeMs=600]
 * @param {Function} [onEnd]
 */
export function playFullscreenVideoOnce(src, { muted = false, fadeMs = 600 } = {}, onEnd) {
    const wrap = document.createElement("div");
    wrap.style.cssText = `
    position: fixed; inset: 0; z-index: 999998; background: #000; opacity: 0; transition: opacity ${fadeMs}ms ease;
    display: flex; align-items: center; justify-content: center;
  `;

    const video = document.createElement("video");
    video.src = src;
    video.playsInline = true;
    video.autoplay = true;
    video.muted = !!muted;
    video.controls = false;
    video.loop = false;
    video.style.cssText = `
    max-width: 100vw; max-height: 100vh; outline: none; display: block;
  `;

    video.addEventListener("ended", () => {
        wrap.style.opacity = "0";
        setTimeout(() => {
            wrap.remove();
            if (typeof onEnd === "function") onEnd();
        }, fadeMs);
    });

    wrap.appendChild(video);
    document.body.appendChild(wrap);
    // Fade in
    requestAnimationFrame(() => (wrap.style.opacity = "1"));
    return video;
}

// Optional global access if you prefer calling from anywhere without imports
if (typeof window !== "undefined") {
    window.runPageCollapse = runPageCollapse;
    window.playFullscreenVideoOnce = playFullscreenVideoOnce;
}
